import React, { useState, useEffect, useMemo } from 'react';
import { Table, Justify, StatusTip, Pagination, Bubble } from 'tea-component';
import SearchRecent from '@/pages/intelligence/components/SearchRecent';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';
import { DetailsCloums } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/CommonCloums';
import FirstRow from '@/pages/intelligence/components/TableComponents/components/FirstRow';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { getDetailGamePathCommon } from '@/pages/intelligence/utils/topchartsUtils';
import { getDetailGameOverviewPath } from '@/pages/intelligence/utils/topchartsUtils';
import SortBy, {
  SortByItemType,
} from '@/pages/intelligence/components/TableComponents/components/SortBy';
import { newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import { transTableDataForSearch } from '../../_config/utils';
import {
  MetricsSort,
  StreamSourceSort,
  TimeScaleSort,
  yearList,
  monthList,
  SocialChannelLabel,
  GLOBAL_REGION,
  SocialChannelsFiledList,
  PlayerActivityFiledList,
} from '../../_config/constant';
import { getMobileBenchmarkRankList } from '../../../../services/mobileGameBenchmark';

import {
  dbiBtnLogMes,
  dbiPageLogMes,
} from '@/pages/intelligence/utils/buriedPointCfg';
import CloumsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import {
  DATASOURCE_NAME,
  DataSourceBubbleInfo,
} from '@/pages/intelligence/components/DataSourceComponents';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import type { TableColumn, PagingQuery } from 'tea-component';
import { useIntl, history, getLocale } from 'umi';
import BasisInfoColum from './BasicInfo';
import NoData from '@/pages/intelligence/components/NoData';
import ExportTable from '../../../../components/ExportTable';
import { get, isNumber, reduce } from 'lodash-es';
import './index.scss';

const { groupable, scrollable } = Table.addons;
const { LoadingTip } = StatusTip;
import { StatsSegmentType } from '../../types';

interface TableContentProps {
  loading: boolean;
  segment: StatsSegmentType;
  pageSize: number;
  pageIndex: number;
  totalNum: number;
  dataSource: any[];
  filterValue: any;
  apiParams: any;
  levelValue: any;
  source: any;
  regionOptions?: any[];
  onPagingChange: (pageInfo: PagingQuery) => void;
  handleFetchTable: (params: { [key: string]: any }) => void;
}

const TableContent = (props: TableContentProps) => {
  const {
    source,
    segment,
    pageSize,
    pageIndex,
    totalNum,
    onPagingChange,
    loading,
    dataSource,
    filterValue,
    apiParams,
    handleFetchTable,
    levelValue,
    regionOptions = [],
  } = props;
  const [columns, setColumns] = useState<any[]>([]);
  const [headerGroupList, setHeaderGroupList] = useState<any[]>([]);
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const defaultPage = 20;

  const getDetailGamePath = (gameId: string) => {
    return getDetailGamePathCommon({
      entityType: 'mobile',
      gameId: gameId,
      tab: 'stats',
      subTab: 'retention_monthly',
    });
  };

  const onClickGame = (gameId: string) => {
    if (gameId) history.push(getDetailGameOverviewPath(gameId, 'mobile'));
  };

  const onSortChange = (value: SortByItemType) => {
    let orderRegion = {}
    if(segment === 'kpi_data' || segment === 'player_activity'){
      orderRegion = {
        order_region: filterValue?.regions || []
      }
    }
    handleFetchTable({
      order_metric: value.sortMetric,
      order: value.sortVal,
      order_source: segment,
      ...orderRegion
    });
  };

  useEffect(() => {
    let segmentColum: any[] = [];
    switch (segment) {
      case 'kpi_data':
        const config = getKpiDataColumnsConfig();
        segmentColum = config?.columns;
        setHeaderGroupList(config.headerGroupList);
        break;
      case 'social_channels':
        const configSocial = getSocialChannelsColumns();
        segmentColum = configSocial?.columns;
        setHeaderGroupList(configSocial.headerGroupList);
        break;
      case 'player_activity':
        const configPlayer = getPlayerActivityColumns();
        segmentColum = configPlayer?.columns;
        setHeaderGroupList(configPlayer.headerGroupList);
        break;

      case 'basic_info':
        segmentColum = BasisInfoColum(
          formatMessage,
          onSortChange,
          apiParams,
          locale,
        );
        setHeaderGroupList([]);
        break;
      default:
        segmentColum = [];
        setHeaderGroupList([]);
        break;
    }
    const columnsList = [
      {
        key: 'gametitle',
        header: (
          <div className="dbi-firstRow-flex">
            <div className="dbi-text-id">#</div>
            <div>{formatMessage({ id: 'dbi_top_gametitle' })}</div>
          </div>
        ),
        width: 350,
        fixed: 'left',
        render: (cvm: any) => (
          <FirstRow
            id={cvm?.rank}
            cvm={{
              img: cvm?.cover,
              gametitle: cvm?.name,
              countryIcon: getCountryFlag(cvm?.publisher_addr),
              text: cvm?.publisher,
            }}
            rihgtClickToJumpHref={getDetailGamePath(cvm?.app_id)}
            onClick={() => {
              onClickGame(cvm.app_id);
            }}
          />
        ),
      },
      ...segmentColum,
      DetailsCloums({
        onClickGame: (cvm: any) => {
          onClickGame(cvm?.app_id);
        },
        formatMessage,
      }),
    ];
    setColumns(columnsList);
  }, [segment, locale, filterValue, apiParams, source]);

  const goSourceMetrics = (source: string, metrics: string) => {
    if (source === 'appannie') {
      if (metrics === 'revenue') {
        history.push('/intelligence/sourcesMetrics/metrics?gameType=mobile&pageid=appannie&metric=Revenue,Monthly Revenue');
      }
      if (metrics === 'download') {
        history.push('/intelligence/sourcesMetrics/metrics?gameType=mobile&pageid=appannie&metric=Downloads,Monthly Downloads');
      }

      if (metrics === 'dau') {
        history.push('/intelligence/sourcesMetrics/metrics?gameType=mobile&pageid=appannie&metric=DAU');
      }
    }
  };

  const getKpiDataColumnsConfig = () => {
    let headerGroupList: any[] = [];
    let columns: any[] = [];
    const metricsList = filterValue?.metrics || [];
    const timePeriodList =
      filterValue?.time_period === '1y~5y' ? yearList : monthList;

    // 指标排序
    const sortMetricsList = metricsList?.sort(
      (a, b) => MetricsSort[a] - MetricsSort[b],
    );
    const dataSourceIcon = getDataSourceIcon(source);
    sortMetricsList?.forEach((item) => {
      headerGroupList.push({
        key: item,
        title: (
          <div
            className="dbi-mobile-benchmark-header-title"
            style={{ color: '#262832' }}
          >
            <Bubble
              dark
              style={{ maxWidth: 400 }}
              content={
                source === 'databrain' ? (
                  // <div style={{ display: 'flex' }}>
                  //   {formatMessage(
                  //     { id: 'dbi_benchmark_source_info' },
                  //     {
                  //       type: formatMessage({
                  //         id: `dbi_benchmark_${item}_mobile_lower`,
                  //       }),
                  //     },
                  //   )}
                  //   <img src={dataSourceIcon} style={{ margin: '0 4px' }} />
                  //   {formatMessage({ id: 'dbi_benchmark_databrain' })}
                  // </div>
                  null
                ) : (
                  <DataSourceBubbleInfo
                    customActions={() => goSourceMetrics(source, item)}
                    dataOfDom={formatMessage({
                      id: `dbi_benchmark_table_${item}_lower`,
                    })}
                    comesFormDom={
                      <>
                        <img
                          src={dataSourceIcon}
                          style={{ verticalAlign: 'middle', marginRight: 4 }}
                        />
                        {DATASOURCE_NAME[source]}
                      </>
                    }
                  />
                )
              }
            >
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={dataSourceIcon}
                  alt=""
                  width={20}
                  style={{ marginRight: 4 }}
                />
                {formatMessage({
                  id: `dbi_benchmark_${item}_mobile`,
                })}
              </div>
           
            </Bubble>
           
          </div>
        ),
        algin: 'center',
        children: timePeriodList?.map((config) => `${item}_${config?.value}`),
      });
      timePeriodList?.forEach((config) => {
        const name =
          locale === 'zh-CN' ? config?.name_zh || config.name : config.name;
        columns.push({
          key: `${item}_${config?.value}`,
          align: 'center',
          width: 120,
          header: () => {
            return (
              <CloumsCenter align="center">
                <SortBy
                  title={name}
                  list={[
                    {
                      key: `${item}_${config?.value}`,
                      sortMetric: `${item}_${config?.value}`,
                      text: `${name}`,
                      title: `${name}`,
                    },
                  ]}
                  defaultKey={apiParams?.order_metric}
                  defaultValue={apiParams?.order}
                  onChange={onSortChange}
                />
              </CloumsCenter>
            );
          },
          render: (row: any) => {
            const value = row?.metric_map?.[`${item}_${config?.value}`]?.value;
            return (
              <div
                style={{
                  textAlign: 'right',
                }}
              >
                {value ? newMathFormat(value, true) : '-'}
              </div>
            );
          },
        });
      });
    });

    return {
      headerGroupList,
      columns,
    };
  };

  const getSocialChannelsColumns = () => {
    let columns: any[] = [];
    let headerGroupList: any[] = [];
    const steamSourceList = filterValue?.steam_source || [];
    const timeScaleList = filterValue?.time_scale || [];

    const steamSourceSortList = steamSourceList?.sort(
      (a, b) => StreamSourceSort[a] - StreamSourceSort[b],
    );

    const timeScaleSortList = timeScaleList?.sort(
      (a, b) => TimeScaleSort[a] - TimeScaleSort[b],
    );
    steamSourceSortList?.forEach((item) => {
      const itemName = formatMessage({
        id: SocialChannelLabel?.[item] || '',
      });
      headerGroupList.push({
        key: item,
        title: (
          <div style={{ textAlign: 'center', color: '#262832' }}>
            {itemName}
          </div>
        ),
        algin: 'center',
        children: timeScaleSortList?.map((config) => `${item}_${config}`),
      });
      timeScaleSortList?.forEach((scale) => {
        const scaleName = formatMessage({
          id: SocialChannelLabel?.[scale] || '',
        });
        headerGroupList.push({
          key: `${item}_${scale}`,
          title: (
            <div style={{ textAlign: 'center', color: '#262832' }}>
              {scaleName}
            </div>
          ),
          algin: 'center',
          children: SocialChannelsFiledList?.map(
            (config) => `${item}_${scale}_${config?.value}`,
          ),
        });
        SocialChannelsFiledList?.forEach((filed) => {
          const filedName = locale === 'zh-CN' ? filed?.name_zh : filed?.name;
          // const titleName = `${itemName} ${scaleName} ${filedName}`;
          columns.push({
            key: `${item}_${scale}_${filed?.value}`,
            align: 'center',
            width: filed.width,
            header: () => {
              return (
                <CloumsCenter align="center" width={filed.width}>
                  <SortBy
                    title={filedName}
                    list={[
                      {
                        key: `${item}_${scale}_${filed?.value}`,
                        sortMetric: `${item}_${scale}_${filed?.value}`,
                        text: filedName,
                        title: filedName,
                      },
                    ]}
                    defaultKey={apiParams?.order_metric}
                    defaultValue={apiParams?.order}
                    onChange={onSortChange}
                  />
                </CloumsCenter>
              );
            },
            render: (row: any) => {
              const value = get(
                row,
                `metric_map.${item}_${scale}_${filed?.value}.value`,
                '',
              );
              return (
                <div
                  style={{
                    textAlign: 'right',
                  }}
                >
                  {value ? newMathFormat(value, true) : '-'}
                </div>
              );
            },
          });
        });
      });
    });
    return { columns, headerGroupList };
  };

  const getPlayerActivityColumns = () => {
    let columns: any[] = [];
    let headerGroupList: any[] = [];
    const regionsList = filterValue?.regions || [];
    regionsList?.forEach((reg) => {
      const allRegion = [...GLOBAL_REGION, ...regionOptions];
      let regionName = reg;
      const targetRegion = allRegion?.find(
        (config) => config?.area_code === reg,
      );

      if (targetRegion) {
        regionName =
          locale === 'zh-CN'
            ? targetRegion?.area_name_ch
            : targetRegion?.area_name_en;
      }

      headerGroupList.push({
        key: reg,
        title: (
          <div style={{ textAlign: 'center', color: '#262832' }}>
            {regionName}
          </div>
        ),
        algin: 'center',
        children: PlayerActivityFiledList?.map(
          (config) => `${reg}_${config?.value}`,
        ),
      });
      PlayerActivityFiledList?.forEach((item) => {
        columns.push({
          key: `${reg}_${item.value}`,
          align: 'center',
          width: item.width,
          header: () => {
            return (
              <CloumsCenter align="center" width={item.width}>
                <SortBy
                  title={locale === 'zh-CN' ? item?.name_zh : item?.name}
                  list={[
                    {
                      key: `${reg}_${item.value}`,
                      sortMetric: `${reg}_${item.value}`,
                      text: locale === 'zh-CN' ? item?.name_zh : item?.name,
                      title: locale === 'zh-CN' ? item?.name_zh : item?.name,
                    },
                  ]}
                  defaultKey={apiParams?.order_metric}
                  defaultValue={apiParams?.order}
                  onChange={onSortChange}
                />
              </CloumsCenter>
            );
          },
          render: (cvm: any) => {
            const value = get(cvm, `metric_map.${reg}_${item.value}.value`, '');
            let displayName = '-';

            if (!value) {
              displayName = '-';
            } else if (value === '0') {
              displayName = '0';
            } else if (isNumber(Number(value)) && value !== '0') {
              if(['ltv', 'arpdau', 'avg_play_days'].includes(item.value)){
                displayName = isNaN(Number(value))
                ? '-'
                : `${Number(value).toFixed(2)}`;
              }else{
                displayName = isNaN(Number(value))
                ? '-'
                : `${Number(value).toFixed(2)}%`;
              }
            }
            return (
              <div
                style={{
                  textAlign: 'right',
                }}
              >
                {displayName}
              </div>
            );
          },
        });
      });
    });

    return {
      columns,
      headerGroupList,
    };
  };

  const downloadTableParams = useMemo(() => {
    const commonParams = {
      ...apiParams,
      entity_type: 'mobile',
      //file_name: 'mobile_benchmark',
    };
    switch (segment) {
      case 'kpi_data':
        return {
          metrics: filterValue?.metrics || [],
          ...commonParams,
        };
      case 'social_channels':
        return {
          steam_source: filterValue?.steam_source || [],
          time_scale: filterValue?.time_scale || [],
          ...commonParams,
        };
      case 'basic_info':
      case 'player_activity':
      default:
        return commonParams;
    }
  }, [segment, filterValue, apiParams]);

 const levelGameList = useMemo(()=> {
  const gameIdList = reduce(
    levelValue,
    (result, value: string[]) => {
      result.push(...value);
      return result;
    },
    [] as any,
  );
  if(Array.isArray(gameIdList)) return gameIdList
  return []
 }, [levelValue])
 

  return (
    <>
      <Table.ActionPanel
        style={{
          // marginTop: segment === 'basic_info' ? '-56px' : 0,
          marginBottom: '20px',
        }}
      >
        <Justify
          left={<></>}
          right={
            <>
              <div>
                <SearchRecent
                  fetchParams={apiParams}
                  searchType="mobile-benchmark"
                  pageLogInfo={dbiPageLogMes?.mobileGameBenchmarkFigure}
                  handleClear={() => handleFetchTable({ unified_ids: [] })}
                  getTableResultList={(id: string) =>
                    handleFetchTable({ page: 1, unified_ids: [id] })
                  }
                  searchListApi={getMobileBenchmarkRankList}
                  transTableDataForSearch={transTableDataForSearch}
                />
                <ExportTable
                  downloadModalSize={segment === 'basic_info' ? 500 : 820}
                  entityType={'mobile'}
                  businessType={`mobile-benchmark-${segment}`}
                  isDisableDownload={loading}
                  downloadLogConfigKey="mobileGameBenchmarkDownloadExcel"
                  tableParams={downloadTableParams}
                  total={totalNum}
                />
              </div>
            </>
          }
        ></Justify>
      </Table.ActionPanel>
      <CardSlotV2>
        {loading ? (
          <div
            className="dbi-benchmark-table-loading"
            style={{ background: '#fff' }}
          >
            <LoadingTip />
          </div>
        ) : dataSource?.length > 0 ? (
          <div className="dbi-benchmark-table mobile-benchmark-table">
            <Table
              recordKey={'app_id'}
              className={`dbi-table-sticky ${
                segment !== 'basic_info'
                  ? 'mobile-kpi-data-table us_digital_revenue-table'
                  : ''
              }`}
              columns={columns}
              records={dataSource}
              bordered={segment !== 'basic_info' ? 'all' : false}
              addons={[
                groupable({
                  headerGroups: headerGroupList,
                }),
              ]}
            />
            <Pagination
              pageSize={pageSize}
              pageIndex={pageIndex}
              onPagingChange={onPagingChange}
              recordCount={loading ? 0 : totalNum}
              pageSizeOptions={[defaultPage, 50, 100]}
              pageSizeVisible={totalNum > defaultPage}
              pageIndexVisible={totalNum > defaultPage}
              jumpVisible={totalNum > defaultPage}
              endJumpVisible={totalNum > defaultPage}
            />
          </div>
        ) : (
          <div style={{ height: 200 }}>
            <NoData />
          </div>
        )}
      </CardSlotV2>
    </>
  );
};

export default TableContent;
